import { hexToRgb } from "@mozioinc/utils";
import merge from "lodash.merge";
import { defaultTheme } from "../src/utils/theme";
import type {
  WhiteLabelInjectData,
  WhiteLabelDomainData,
} from "@mozioinc/query";

export type EsimWhiteLabelInjectData = WhiteLabelInjectData & {
  config: WhiteLabelInjectData["config"] & {
    header: { logo: string; showSignInButton: boolean };
    footer: {
      logo: string;
      phoneNumber: { default: string } & Record<string, string>; // the key is either default or a locale
      emailLink: string;
      socials?: {
        facebook?: string;
        twitter?: string;
        instagram?: string;
        linkedin?: string;
      };
      navLinks?: {
        href: string;
        content: string;
      }[];
      termsAndPrivacyLinks?: {
        href: string;
        content: string;
      }[];
    };
    heroImageUrl?: string;
  };
};

export const defaultWhiteLabelData: EsimWhiteLabelInjectData = {
  metadata: {
    title: "eSIM",
    description: "",
    keywords: "eSIM, Mozio",
    imageUrl: "https://static.mozio.com/assets/mozio-extra-logo.svg",
    faviconUrl: "https://static.mozio.com/assets/favicon.ico",
    canonicalUrl: "https://www.mozio.com/esim",
    url: "https://www.mozio.com/esim",
  },
  themeVars: defaultTheme,
  config: {
    name: "mozio",
    partnerRef: "mozio",
    domain: "www.mozio.com",
    locale: "en_US",
    currency: "USD",
    authRequired: false,
    header: {
      logo: "https://static.mozio.com/assets/mozio-extra-logo.svg",
      showSignInButton: false,
    },
    footer: {
      logo: "https://static.mozio.com/assets/logo.svg",
      phoneNumber: {
        default: "+1 (855) 980-7116",
      },
      emailLink: "travel-extras@mozio.com",
    },
  },
};

export const mapWhiteLabelInjectData = (
  data: WhiteLabelDomainData,
): EsimWhiteLabelInjectData => {
  const { domain, locale, currency, config } = data;

  return merge({}, defaultWhiteLabelData, {
    metadata: {
      imageUrl: config.CUSTOM_SOCIAL_LOGO,
      faviconUrl: config.CUSTOM_FAVICON,
    },
    themeVars: {
      "--mz-color-primary": hexToRgb(config.FIRST_BACKGROUND_COLOR),
      "--mz-color-secondary": hexToRgb(config.CTA_BACKGROUND_COLOR),
      "--mz-color-primary-contrast": hexToRgb(config.FIRST_CONTRAST_COLOR),
      "--mz-color-secondary-contrast": hexToRgb(config.CTA_CONTRAST_COLOR),
      "--mz-header-height": config.HEADER_CUSTOM_HEIGHT || undefined,
      "--mz-header-logo-height": config.HEADER_CUSTOM_LOGO_HEIGHT || undefined,
    },
    config: {
      domain,
      locale,
      currency,
      partnerRef: config.PARTNER_REF,
      heroImageUrl: config.ESIM_COVER_IMAGE,
      authRequired: config.ALLOW_ONLY_REGISTERED,
      header: {
        logo: config.CUSTOM_LOGO || undefined,
        showSignInButton: config.SHOW_SIGNIN_BUTTON,
      },
      footer: {
        logo: config.CUSTOM_FOOTER_LOGO || undefined,
        socials: Object.fromEntries(config.FOOTER_SOCIALS),
        phoneNumber: config.ESIM_SUPPORT_PHONE_NUMBERS
          ? Object.fromEntries(config.ESIM_SUPPORT_PHONE_NUMBERS)
          : undefined,
        emailLink: config.ESIM_SUPPORT_EMAIL,
      },
    },
  });
};
